import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, AuthState, LoginCredentials, RegisterData } from '../types';
import { supabase, supabaseHelpers } from '../services/supabase';
import { config } from '../utils/config';
import { setCachedAuthToken } from '../services/api/client';
import { resetAllStores } from '../stores';
import { useDeskStore } from '../stores/deskStore';
import { useDocumentStore } from '../stores/documentStore';
import { queryClient } from '../queries/queryClient';
import { mapAuthError, extractAuthErrorCode } from '../utils/authErrors';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  resendSignupConfirmation: (email: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    errorCode: null,
  });

  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Safety timeout — if onAuthStateChange never fires (broken storage,
    // extension interference, etc.), stop loading so the user at least gets
    // redirected to login instead of an infinite spinner.
    const safetyTimer = setTimeout(() => {
      setAuthState((prev) => {
        if (prev.loading) {
          console.warn('[Auth] Safety timeout — forcing loading:false');
          return { user: null, loading: false, error: null, errorCode: null };
        }
        return prev;
      });
    }, 10000);

    // Single auth source: onAuthStateChange fires INITIAL_SESSION on
    // subscription (auth-js calls getSession() internally once), then fires
    // on every login/logout/token refresh. No manual getSession() call needed.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      clearTimeout(safetyTimer);

      // Push token to API client immediately — this is the SOLE token source
      // for the axios interceptor. No onAuthStateChange in client.ts. (#565)
      setCachedAuthToken(session?.access_token ?? null);

      const newUserId = session?.user?.id ?? null;
      const previousUserId = previousUserIdRef.current;
      const persistedUserId = useDeskStore.getState().persistedUserId;

      // Clear all stores and query cache when user changes.
      // Covers both in-session switches (previousUserId differs) and
      // page refreshes where localStorage has a different user's state.
      const userChanged = (previousUserId && previousUserId !== newUserId)
        || (newUserId && persistedUserId && persistedUserId !== newUserId);
      // Also reset on logout (session becomes null). Without this, logging
      // out and back in as the SAME user leaves stale desk state around —
      // e.g. a split-screen layout from the previous session rehydrates
      // with no documents in it, since selectedMatterId was cleared but
      // splitMode was not. Matches what the user expects from "log out".
      const loggedOut = previousUserId && !newUserId;
      if (userChanged || loggedOut) {
        resetAllStores();
        queryClient.clear();
      }

      // Plan 40 / #794: defense in depth on the "not-logged-in -> logged-in"
      // transition. This is NOT a full session reset — it intentionally
      // resets only transient right-panel/clause UI state, preserving
      // persisted selectedMatterId, openDocuments, etc. that a same-user
      // re-login should keep. The full reset path stays gated on
      // (userChanged || loggedOut) above. Catches any stale transient state
      // a future code path leaves behind without going through resetAllStores.
      // See memory note `project-right-panel-view-stack` for the rule.
      const transitionedToLoggedIn = !previousUserId && newUserId;
      if (transitionedToLoggedIn) {
        useDeskStore.getState().resetRightPanelToChat();
        useDocumentStore.getState().clearClauseSelection();
      }

      // Track current user in both the ref (in-session) and store (cross-refresh)
      previousUserIdRef.current = newUserId;
      if (newUserId) {
        useDeskStore.getState().setPersistedUserId(newUserId);
      }

      if (session?.user) {
        // IMPORTANT: Do NOT use supabase.from() here! supabase.from() calls
        // getSession() internally to get the access token, which tries to
        // re-acquire auth-js's Web Lock. But this callback runs INSIDE
        // _emitInitialSession which already holds that lock. The re-entrant
        // lock queue creates a circular dependency → deadlock → loading
        // stays true forever. See Issue #565 root cause analysis.
        //
        // Instead, use fetch() directly with the session's access token
        // (already available as a parameter), bypassing the lock entirely.
        let is_admin = false;
        try {
          const res = await fetch(
            `${config.supabaseUrl}/rest/v1/users?select=is_admin&id=eq.${session.user.id}`,
            {
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': config.supabaseAnonKey,
                'Accept': 'application/json',
              },
            }
          );
          if (res.ok) {
            const rows = await res.json();
            is_admin = rows?.[0]?.is_admin ?? false;
          } else {
            console.warn('Failed to fetch is_admin:', res.status);
          }
        } catch (e) { console.warn('is_admin fetch exception:', e); }
        setAuthState({
          user: {
            id: session.user.id,
            email: session.user.email!,
            created_at: session.user.created_at,
            is_admin,
          },
          loading: false,
          error: null,
          errorCode: null,
        });
      } else {
        setAuthState({ user: null, loading: false, error: null, errorCode: null });
      }
    });

    return () => {
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null, errorCode: null }));
      await supabaseHelpers.signIn(credentials.email, credentials.password);
      // State update happens via onAuthStateChange listener
    } catch (error) {
      const raw = error instanceof Error ? error.message : 'Login failed';
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: mapAuthError(raw),
        errorCode: extractAuthErrorCode(error, raw),
      }));
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null, errorCode: null }));
      await supabaseHelpers.signInWithGoogle();
      // Browser navigates away to Google before this resolves. If it does
      // resolve here (e.g. SDK rejects redirect), the catch below handles it.
    } catch (error) {
      const raw = error instanceof Error ? error.message : 'Google sign-in failed';
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: mapAuthError(raw),
        errorCode: extractAuthErrorCode(error, raw),
      }));
      throw error;
    }
  };

  const register = async (userData: RegisterData): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null, errorCode: null }));
      const data = await supabaseHelpers.signUp(userData.email, userData.password);
      // Supabase obfuscates already-registered emails by returning a fake-looking
      // signup-success with NO session AND an empty identities array. Gate on
      // the conjunction so we encode both invariants — protects against any
      // future shape change where identities: [] appears alongside a real session.
      // Defensive: undefined identities falls through to CONFIRMATION_REQUIRED.
      // See Plan 210 + docs/user-authentication.md.
      const identities = data.user?.identities;
      if (
        !data.session &&
        Array.isArray(identities) &&
        identities.length === 0
      ) {
        setAuthState(prev => ({ ...prev, loading: false }));
        throw new Error('EMAIL_ALREADY_REGISTERED');
      }
      // If no session returned, email confirmation is required
      if (!data.session) {
        setAuthState(prev => ({ ...prev, loading: false }));
        throw new Error('CONFIRMATION_REQUIRED');
      }
      // Otherwise, state update happens via onAuthStateChange listener
    } catch (error) {
      const raw = error instanceof Error ? error.message : 'Registration failed';
      const code = (error as { code?: string } | null | undefined)?.code;

      // Belt-and-suspenders: Supabase can return an explicit
      // "User already registered" error (or coded user_already_exists) instead
      // of the obfuscated fake-user response, depending on Confirm-email config
      // and GoTrue version. Re-throw as the same sentinel so the
      // AlreadyRegistered screen catches both paths.
      if (raw === 'User already registered' || code === 'user_already_exists') {
        setAuthState(prev => ({ ...prev, loading: false }));
        throw new Error('EMAIL_ALREADY_REGISTERED');
      }

      // Both sentinels are control-flow markers, not error messages.
      if (raw !== 'CONFIRMATION_REQUIRED' && raw !== 'EMAIL_ALREADY_REGISTERED') {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: mapAuthError(raw),
          errorCode: extractAuthErrorCode(error, raw),
        }));
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null, errorCode: null }));
      await supabaseHelpers.signOut();
      // State update happens via onAuthStateChange listener
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      setAuthState(prev => ({ ...prev, loading: false, error: errorMessage, errorCode: null }));
      throw error;
    }
  };

  const resendSignupConfirmation = async (email: string) => {
    // Intentionally does not touch authState.loading — the resend surfaces
    // (Register confirmation screen, Login inline affordance) manage their
    // own button state and cooldown. Errors are thrown to the caller so they
    // can render inline rate-limit / generic messages via isResendRateLimitError.
    await supabaseHelpers.resendSignupConfirmation(email);
  };

  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null, errorCode: null }));
  };

  const value: AuthContextType = {
    ...authState,
    login,
    loginWithGoogle,
    register,
    logout,
    resendSignupConfirmation,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
